import { cn } from "@/lib/utils";

/**
 * FloatingAccents Component
 * 
 * Decorative floating shapes (triangles and circles) positioned at section edges.
 * Provides visual rhythm and separation between sections.
 * Part of the LEAP design system's decorative elements.
 * 
 * @param className - Additional CSS classes for container
 * @param position - Where to show accents ("top", "bottom", or "both")
 * 
 * @example
 * <FloatingAccents position="top" />
 */
interface FloatingAccentsProps {
  className?: string;
  position?: "top" | "bottom" | "both";
}

export const FloatingAccents = ({ 
  className,
  position = "both"
}: FloatingAccentsProps) => {
  return (
    <div className={cn("absolute inset-0 overflow-hidden pointer-events-none", className)}>
      {/* Top Accents */}
      {(position === "top" || position === "both") && (
        <>
          {/* Top Left Triangle */}
          <div 
            className="absolute -top-4 left-8 w-8 h-8 opacity-20 animate-float"
            style={{ animationDelay: "0s", animationDuration: "4s" }}
          >
            <div 
              className="w-full h-full bg-brand-teal"
              style={{ clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)" }}
            />
          </div>

          {/* Top Right Circle */}
          <div 
            className="absolute top-12 right-16 w-6 h-6 opacity-20 animate-float"
            style={{ animationDelay: "1s", animationDuration: "5s" }}
          >
            <div className="w-full h-full bg-brand-orange rounded-full" />
          </div>

          {/* Top Center Small Triangle */}
          <div 
            className="absolute top-20 left-1/3 w-5 h-5 opacity-15 animate-float"
            style={{ animationDelay: "2s", animationDuration: "4.5s" }}
          >
            <div 
              className="w-full h-full bg-brand-purple"
              style={{ clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)" }}
            />
          </div>
        </>
      )}

      {/* Bottom Accents */}
      {(position === "bottom" || position === "both") && (
        <>
          {/* Bottom Right Triangle */}
          <div 
            className="absolute -bottom-4 right-12 w-10 h-10 opacity-20 animate-float"
            style={{ animationDelay: "0.5s", animationDuration: "4.5s" }}
          >
            <div 
              className="w-full h-full bg-brand-purple transform rotate-180"
              style={{ clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)" }}
            />
          </div>

          {/* Bottom Left Circle */}
          <div 
            className="absolute bottom-16 left-20 w-7 h-7 opacity-20 animate-float"
            style={{ animationDelay: "1.5s", animationDuration: "5s" }}
          >
            <div className="w-full h-full bg-brand-green rounded-full" />
          </div>

          {/* Bottom Center Small Circle */}
          <div 
            className="absolute bottom-24 right-1/4 w-4 h-4 opacity-15 animate-float"
            style={{ animationDelay: "2.5s", animationDuration: "4s" }}
          >
            <div className="w-full h-full bg-brand-teal rounded-full" />
          </div>
        </>
      )}
    </div>
  );
};
